import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api';
import { dictionariesApi, type DictionaryValue } from '../../lib/dictionariesApi';
import { featureFlags } from '../../lib/featureFlags';
import { formatCellValue, parseEditedValue } from '../../lib/fieldFormat';
import {
  layersApi,
  type FeatureDetail,
  type FeatureLinkInfo,
  type LayerDetail,
  type LayerFeature,
  type LayerFieldDetail,
} from '../../lib/layersApi';

// Не только 'name'/'title'/'название' — у слоёв с исходными МРСК-данными и
// демо-слоями естественный идентификатор часто лежит под другим кодом поля
// (см. аудит слоёв в MEMORY/чате). Без этого pickObjectTitle откатывался на
// "первое строковое свойство", что для однотипных объектов (все "светильники"
// с одинаковым 'Тип светильника' и т.п.) показывало одно и то же название —
// невозможно было понять, какой конкретно объект выбираешь в пикере.
const KNOWN_TITLE_KEYS = new Set([
  'name',
  'title',
  'название',
  'article', // Светильники — артикул
  'structure_number', // Конструкции — инвентарный номер
  'serial_number', // Устройства управления линией — серийный номер
  'dname', // Линии ЛЭП / ТП и РП / Подстанции — официальное обозначение
  'number1', // Опоры ЛЭП — "Опора № N"
  'nazvanie', // старый демо-слой "Демо здания"
  'adress', // Точки Wi-Fi
  'STATE_NAME', // демо-слой states (GeoServer)
]);

export const geometryTypeLabels: Record<string, string> = {
  Point: 'Точка',
  MultiPoint: 'Мультиточка',
  LineString: 'Линия',
  MultiLineString: 'Мультилиния',
  Polygon: 'Полигон',
  MultiPolygon: 'Мультиполигон',
};

// A dictionary/reference field (condition, fixture_type, meter_feature_id, …)
// stores the referenced value's raw uuid as its property value — a perfectly
// good property, just not a name. Without this, the "first string property"
// fallback below would happily pick that uuid up and show it as the object's
// title (reported: typed a real название, still saw a uuid) whenever it
// happened to be the first string-valued key. `_`-prefixed keys are internal
// bookkeeping (e.g. _parent_feature_id, _clusterLayerId) and excluded outright.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isDisplayableTitleValue(key: string, value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && !key.startsWith('_') && !UUID_RE.test(value);
}

export function pickObjectTitle(properties: Record<string, unknown>): string {
  for (const key of KNOWN_TITLE_KEYS) {
    const v = properties[key];
    if (isDisplayableTitleValue(key, v)) return v;
  }
  const firstEntry = Object.entries(properties).find(([key, v]) => isDisplayableTitleValue(key, v));
  return (firstEntry?.[1] as string | undefined) ?? 'Объект без названия';
}

/** All data/editing state for one object's card — shared by the full page
 * (ObjectCardBody, opened from the layer table) and the map's big popup
 * (ObjectCardModal), which render it completely differently but need the
 * exact same fetching/staged-edit/delete/sibling-navigation behavior. */
export function useObjectCard(layerId: string, featureId: string, onNavigateFeature: (id: string) => void) {
  const [layer, setLayer] = useState<LayerDetail | null>(null);
  const [feature, setFeature] = useState<FeatureDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [siblingFeatures, setSiblingFeatures] = useState<LayerFeature[]>([]);
  const [stripSearch, setStripSearch] = useState('');

  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  // Staged edits — nothing is sent to the server until an explicit save.
  const [pending, setPending] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [dictValuesByDict, setDictValuesByDict] = useState<Record<string, DictionaryValue[]>>({});

  // Прототип под тендер — см. src/lib/featureFlags.ts (objectHierarchy).
  const [children, setChildren] = useState<FeatureLinkInfo[]>([]);
  const [parentSaving, setParentSaving] = useState(false);
  const [parentError, setParentError] = useState<string | null>(null);

  const refreshChildren = () => {
    if (!featureFlags.objectHierarchy) return;
    layersApi
      .getFeatureChildren(layerId, featureId)
      .then(setChildren)
      .catch(() => setChildren([]));
  };

  useEffect(refreshChildren, [layerId, featureId]);

  const setParent = async (parentFeatureId: string | null, snapToParent = false) => {
    setParentSaving(true);
    setParentError(null);
    try {
      const updated = await layersApi.setFeatureParent(layerId, featureId, parentFeatureId, snapToParent);
      // snapToParent also moves this object's own geometry — reflect that in the
      // loaded feature, not just the parent link, or the card would show a stale
      // position until the next full reload.
      setFeature((prev) => (prev ? { ...prev, parent: updated.parent, geometry: updated.geometry } : prev));
    } catch (err) {
      setParentError(err instanceof ApiError ? err.message : 'Не удалось изменить родительский объект.');
    } finally {
      setParentSaving(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    setPending({});
    setEditingCode(null);
    Promise.all([layersApi.getLayer(layerId), layersApi.getFeature(layerId, featureId)])
      .then(([layerDetail, featureDetail]) => {
        setLayer(layerDetail);
        setFeature(featureDetail);
      })
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить объект.'))
      .finally(() => setLoading(false));
  }, [layerId, featureId]);

  useEffect(() => {
    layersApi
      .getFeatures(layerId)
      .then((fc) => setSiblingFeatures(fc.features))
      .catch(() => setSiblingFeatures([]));
  }, [layerId]);

  const canEdit = layer?.can_edit_features ?? false;
  const dictionaryFields = layer?.fields.filter((f) => f.data_type === 'dictionary' && f.dictionary_id) ?? [];

  useEffect(() => {
    for (const f of dictionaryFields) {
      const dictId = f.dictionary_id as string;
      if (dictValuesByDict[dictId]) continue;
      dictionariesApi
        .listValues(dictId)
        .then((vals) => setDictValuesByDict((prev) => ({ ...prev, [dictId]: vals })))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer?.id]);

  const effectiveValue = (code: string): unknown => (code in pending ? pending[code] : feature?.properties[code]);

  const stageValue = (field: LayerFieldDetail, nextValue: unknown) => {
    const original = feature?.properties[field.code] ?? null;
    setPending((prev) => {
      const next = { ...prev };
      if (nextValue === original) delete next[field.code];
      else next[field.code] = nextValue;
      return next;
    });
  };

  const startEdit = (field: LayerFieldDetail) => {
    if (!canEdit || field.data_type === 'boolean') return;
    setEditingCode(field.code);
    setEditError(null);
    const raw = effectiveValue(field.code);
    setEditDraft(raw === null || raw === undefined ? '' : String(raw));
  };

  const commitTextEdit = (field: LayerFieldDetail) => {
    const result = parseEditedValue(editDraft, field.data_type);
    setEditingCode(null);
    if (!result.ok) {
      setEditError(result.error);
      return;
    }
    setEditError(null);
    stageValue(field, result.value);
  };

  const handleDiscard = () => {
    setPending({});
    setEditingCode(null);
  };

  const handleSaveAll = async () => {
    if (Object.keys(pending).length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await layersApi.updateFeature(layerId, featureId, pending);
      setFeature((prev) => (prev ? { ...prev, properties: { ...prev.properties, ...updated.properties } } : prev));
      setPending({});
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить изменения.');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPhoto = async (fileId: string) => {
    try {
      const updated = await layersApi.setFeaturePhoto(layerId, featureId, fileId);
      setFeature((prev) => (prev ? { ...prev, photo_file_id: updated.photo_file_id } : prev));
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить фото.');
    }
  };

  // Attachments (see FeatureAttachmentsSection) mutate through their own
  // endpoints directly — this just syncs the resulting properties back into
  // the loaded feature, the same way handleSetPhoto syncs photo_file_id.
  const setFeatureProperties = (properties: Record<string, unknown>) => {
    setFeature((prev) => (prev ? { ...prev, properties } : prev));
  };

  const handleDelete = async (onAfterDelete: () => void) => {
    if (!feature) return;
    if (!window.confirm('Удалить объект? Это действие нельзя отменить.')) return;
    setDeleting(true);
    setSaveError(null);
    try {
      await layersApi.deleteFeature(layerId, featureId);
      onAfterDelete();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось удалить объект.');
      setDeleting(false);
    }
  };

  const confirmDiscardIfNeeded = () => {
    if (Object.keys(pending).length === 0) return true;
    return window.confirm('Есть несохранённые изменения — перейти без сохранения?');
  };

  const goToFeature = (id: string) => {
    if (id === featureId) return;
    if (!confirmDiscardIfNeeded()) return;
    onNavigateFeature(id);
  };

  const filteredSiblings = useMemo(() => {
    const q = stripSearch.trim().toLowerCase();
    if (!q) return siblingFeatures;
    return siblingFeatures.filter((f) => {
      if (pickObjectTitle(f.properties).toLowerCase().includes(q)) return true;
      return Object.values(f.properties).some((v) => formatCellValue(v).toLowerCase().includes(q));
    });
  }, [siblingFeatures, stripSearch]);

  const pendingCount = Object.keys(pending).length;

  return {
    layer,
    feature,
    loading,
    loadError,
    canEdit,
    siblingFeatures,
    filteredSiblings,
    stripSearch,
    setStripSearch,
    editingCode,
    setEditingCode,
    editDraft,
    setEditDraft,
    editError,
    setEditError,
    pending,
    pendingCount,
    saving,
    saveError,
    deleting,
    dictValuesByDict,
    effectiveValue,
    stageValue,
    startEdit,
    commitTextEdit,
    handleDiscard,
    handleSaveAll,
    handleSetPhoto,
    setFeatureProperties,
    handleDelete,
    confirmDiscardIfNeeded,
    goToFeature,
    children,
    setParent,
    refreshChildren,
    parentSaving,
    parentError,
  };
}
